/**
 * Setup roadmap step → how we detect tenant data (auto progress, no manual ticks).
 *
 * @typedef {{ type: 'reference', entity: string }} RefCheck
 * @typedef {{ type: 'referenceAny', entities: string[] }} RefAnyCheck
 * @typedef {{ type: 'suggest', entity: string }} SuggestCheck
 * @typedef {{ type: 'matrix', field: 'riskMatrix' | 'ciaMatrix' }} MatrixCheck
 * @typedef {{ type: 'biaConfig' }} BiaCheck
 * @typedef {{ type: 'dutyList' }} DutyCheck
 * @typedef {{ type: 'exclude' }} ExcludeCheck
 * @typedef {RefCheck | RefAnyCheck | SuggestCheck | MatrixCheck | BiaCheck | DutyCheck | ExcludeCheck} StepCheck
 */

/** @type {Record<string, StepCheck>} key = `${phaseId}.${stepId}` */
export const SETUP_STEP_CHECKS = {
  "foundation.units": { type: "reference", entity: "unit" },
  "foundation.roles": { type: "reference", entity: "role" },
  "foundation.users": { type: "reference", entity: "user" },

  "organization.processes": { type: "reference", entity: "process" },
  "organization.teams": { type: "reference", entity: "team" },
  "organization.duties": { type: "dutyList" },
  "organization.rootCause": { type: "reference", entity: "rootCause" },

  "scales.riskMatrix": { type: "matrix", field: "riskMatrix" },
  "scales.impactProbability": { type: "matrix", field: "riskMatrix" },
  "scales.cia": { type: "matrix", field: "ciaMatrix" },
  "scales.biaScale": { type: "biaConfig" },

  "documents.documentTypes": { type: "reference", entity: "documentType" },
  "documents.classification": { type: "reference", entity: "classification" },

  "inventory.assetGroup": { type: "reference", entity: "assetGroup" },
  "inventory.assetCategory": { type: "reference", entity: "assetCategory" },
  "inventory.assets": { type: "suggest", entity: "asset" },
  "inventory.vendors": { type: "reference", entity: "vendor" },
  "inventory.licenses": { type: "suggest", entity: "assetLicense" },

  "context.context": { type: "suggest", entity: "context" },
  "context.interestedParties": { type: "suggest", entity: "interestedParty" },
  "context.scope": { type: "suggest", entity: "scopeDocument" },
  "context.communication": { type: "suggest", entity: "communication" },

  "riskControls.threats": { type: "reference", entity: "threat" },
  "riskControls.vulnerabilities": { type: "suggest", entity: "vulnerability" },
  "riskControls.riskRegister": { type: "suggest", entity: "riskRegister" },
  "riskControls.riskTreatment": { type: "suggest", entity: "riskTreatment" },
  "riskControls.controls": { type: "suggest", entity: "control" },
  "riskControls.soa": { type: "suggest", entity: "control" },
  "riskControls.objectives": { type: "suggest", entity: "objective" },

  "privacy.kvkkCompany": { type: "suggest", entity: "kvkkCompany" },
  "privacy.kvkkCatalogs": {
    type: "referenceAny",
    entities: ["legalReason", "processingPurpose", "personalDataCategory", "collectionMethod"],
  },
  "privacy.retention": { type: "reference", entity: "privacyRetentionPeriod" },
  "privacy.pdInventory": { type: "suggest", entity: "personalDataInventory" },
  "privacy.consent": { type: "reference", entity: "explicitConsentMethod" },

  "bcm.bia": { type: "suggest", entity: "bia" },
  "bcm.bcp": { type: "suggest", entity: "bcp" },
  "bcm.exercises": { type: "suggest", entity: "exercise" },

  "operations.ticketing": { type: "reference", entity: "ticketCategory" },
  "operations.mailTemplates": { type: "reference", entity: "mailTemplate" },
  "operations.integrations": { type: "suggest", entity: "integrationConnector" },
  "operations.training": { type: "suggest", entity: "training" },
  "operations.about": { type: "exclude" },
};

export function stepCheckKey(phaseId, stepId) {
  return `${phaseId}.${stepId}`;
}

export function hasReferenceRows(entities, seedData, entity) {
  const list = entities?.[entity];
  if (Array.isArray(list) && list.length > 0) return true;
  const seed = seedData?.[entity];
  return Array.isArray(seed) && seed.length > 0;
}

export function isMatrixConfigured(matrixConfiguration, field) {
  const block = matrixConfiguration?.[field];
  if (!block || typeof block !== "object") return false;
  if (field === "riskMatrix") {
    return (block.rows?.length || 0) > 0 && (block.columns?.length || 0) > 0;
  }
  if (field === "ciaMatrix") {
    return (
      (block.confidentialityLevels?.length || 0) > 0 &&
      (block.integrityLevels?.length || 0) > 0 &&
      (block.availabilityLevels?.length || 0) > 0
    );
  }
  return false;
}

export function isBiaConfigPresent(configuration) {
  if (!configuration || typeof configuration !== "object") return false;
  return (configuration.categories?.length || 0) > 0 && (configuration.timeHorizons?.length || 0) > 0;
}

export function getStepCheck(phaseId, stepId) {
  return SETUP_STEP_CHECKS[stepCheckKey(phaseId, stepId)] || { type: "exclude" };
}
